import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown } from "lucide-react";
import { cApi, fmtDate, fmtTime } from "@/lib/community";
import { startGoogleLogin, useCustomerAuth } from "@/context/CustomerAuthContext";

let cache = null;
let cachedAt = 0;
const loadEarlyAccess = (force = false) => {
  if (!cache || force || Date.now() - cachedAt > 60000) {
    cachedAt = Date.now();
    cache = cApi.get("/community/early-access").then(({ data }) => data).catch(() => ({ windows: [], is_prive: false }));
  }
  return cache;
};

function useEarlyAccess(productId) {
  const { user } = useCustomerAuth();
  const [state, setState] = useState({ window: null, isPrive: false });
  useEffect(() => {
    let live = true;
    loadEarlyAccess(true).then((d) => live && setState({ window: d.windows.find((w) => w.product_id === productId) || null, isPrive: d.is_prive }));
    return () => { live = false; };
  }, [productId, user]);
  return state;
}

export function EarlyAccessBadge({ productId }) {
  const [w, setW] = useState(null);
  useEffect(() => {
    loadEarlyAccess().then((d) => setW(d.windows.find((x) => x.product_id === productId) || null));
  }, [productId]);
  if (!w) return null;
  return (
    <span data-testid={`early-access-badge-${productId}`} className="absolute bottom-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 bg-[#2B2522]/85 text-[#F3E1DC] rounded-full">
      <Crown className="w-3 h-3" strokeWidth={1.5} /> Privé Early Access
    </span>
  );
}

const endsLabel = (iso) => `${fmtDate(iso, { weekday: "short", month: "short", day: "numeric" })} at ${fmtTime(iso)}`;

export function EarlyAccessGate({ productId, children }) {
  const { user } = useCustomerAuth();
  const { window: w, isPrive } = useEarlyAccess(productId);
  if (!w) return children;
  if (isPrive)
    return (
      <>
        <p className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A8865A] font-semibold" data-testid="early-access-unlocked">
          <Crown className="w-4 h-4" strokeWidth={1.5} /> Privé Early Access — unlocked for you until {endsLabel(w.ends_at)}
        </p>
        {children}
      </>
    );
  return (
    <div className="mt-8 rounded-2xl bg-[#F3E1DC]/60 border border-[#E7C4BC] p-6" data-testid="early-access-locked">
      <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A8865A] font-semibold"><Crown className="w-4 h-4" strokeWidth={1.5} /> Privé Early Access</p>
      <p className="mt-3 text-sm text-stone-700 leading-relaxed">Our Privé members are shopping this first. It opens to everyone on <strong className="font-medium">{endsLabel(w.ends_at)}</strong>.</p>
      <div className="mt-5 flex flex-wrap gap-3">
        {!user && (
          <button onClick={() => startGoogleLogin(`/product/${productId}`)} className="bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-6 py-3 rounded-full text-xs tracking-wider uppercase" data-testid="early-access-signin-btn">
            Privé member? Sign in
          </button>
        )}
        <Link to="/prive" className="border border-stone-300 hover:border-[#1A1C1A] px-6 py-3 rounded-full text-xs tracking-wider uppercase" data-testid="early-access-prive-link">Discover Privé Club</Link>
      </div>
    </div>
  );
}
